import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Box,
  Chip,
  Collapse,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import SearchIcon from '@mui/icons-material/Search';
import { useQuery } from '@apollo/client/react';
import { GET_WEBHOOK_QUEUES } from '@/graphql/documents/webhookQueues';
import { DetailDataCard, DetailDataCardField } from '@gastrosoft/shared/ui';
import type { TFunction } from 'i18next';

function graphqlErrorMessage(err: unknown): string {
  if (!err || typeof err !== 'object') return '';
  const o = err as { message?: string; graphQLErrors?: ReadonlyArray<{ message: string }> };
  return o.graphQLErrors?.map((e) => e.message).join(' ') || o.message || '';
}

export interface WebhookQueueRow {
  id: string;
  status: string;
  provider?: string | null;
  attempts?: number | null;
  processedAt?: string | null;
  errorMessage?: string | null;
  createdAt: string;
  updatedAt?: string | null;
  payload?: string | null;
  headers?: string | null;
  [key: string]: unknown;
}

type WebhookQueuesQueryData = {
  webhookQueues?: {
    nodes: WebhookQueueRow[] | null;
    totalCount: number;
  } | null;
};

type TimePreset = 'all' | 'today' | 'last7' | 'last30' | 'custom';
type WebhookStatus = 'FAILED' | 'PENDING' | 'PROCESSED' | 'SKIPPED';
type StatusFilter = '' | WebhookStatus;
type WebhookQueueWhere = Record<string, unknown>;

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

function startOfLocalDay(d: Date): Date {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x;
}

function endOfLocalDay(d: Date): Date {
  const x = new Date(d);
  x.setHours(23, 59, 59, 999);
  return x;
}

function parseLocalDateYmd(ymd: string): Date {
  const [y, m, day] = ymd.split('-').map(Number);
  return new Date(y, m - 1, day);
}

function dateRangeForPreset(
  preset: TimePreset,
  customFrom: string,
  customTo: string,
): { gte?: string; lte?: string } {
  const now = new Date();
  if (preset === 'all') return {};
  if (preset === 'today') {
    return {
      gte: startOfLocalDay(now).toISOString(),
      lte: endOfLocalDay(now).toISOString(),
    };
  }
  if (preset === 'last7') {
    const start = new Date(now);
    start.setDate(start.getDate() - 7);
    start.setHours(0, 0, 0, 0);
    return { gte: start.toISOString(), lte: now.toISOString() };
  }
  if (preset === 'last30') {
    const start = new Date(now);
    start.setDate(start.getDate() - 30);
    start.setHours(0, 0, 0, 0);
    return { gte: start.toISOString(), lte: now.toISOString() };
  }
  let gte: string | undefined;
  let lte: string | undefined;
  if (customFrom.trim()) {
    gte = startOfLocalDay(parseLocalDateYmd(customFrom.trim())).toISOString();
  }
  if (customTo.trim()) {
    lte = endOfLocalDay(parseLocalDateYmd(customTo.trim())).toISOString();
  }
  return { gte, lte };
}

function buildEventJobWhere(params: {
  status: StatusFilter;
  timePreset: TimePreset;
  customFrom: string;
  customTo: string;
  search: string;
}): WebhookQueueWhere | undefined {
  const and: WebhookQueueWhere[] = [];
  if (params.status) {
    and.push({ status: { eq: params.status } });
  }
  const { gte, lte } = dateRangeForPreset(params.timePreset, params.customFrom, params.customTo);
  if (gte && lte) and.push({ createdAt: { gte, lte } });
  else if (gte) and.push({ createdAt: { gte } });
  else if (lte) and.push({ createdAt: { lte } });

  const q = params.search.trim();
  if (q) {
    const or: WebhookQueueWhere[] = [
      { provider: { contains: q } },
      { payload: { contains: q } },
      { headers: { contains: q } },
    ];
    if (UUID_RE.test(q)) or.push({ id: { eq: q } });
    and.push({ or });
  }

  if (and.length === 0) return undefined;
  if (and.length === 1) return and[0];
  return { and };
}

function mergeProviderIntoWhere(
  base: WebhookQueueWhere | undefined,
  providerEq: string,
): WebhookQueueWhere | undefined {
  const p = providerEq.trim();
  if (!p) return base;
  const providerFilter: WebhookQueueWhere = { provider: { eq: p } };
  if (!base) return providerFilter;
  return { and: [base, providerFilter] };
}

function stateColor(
  state: string,
): 'default' | 'primary' | 'success' | 'warning' | 'error' | 'info' {
  const u = state.toUpperCase();
  if (u === 'FAILED' || u.includes('FAIL') || u.includes('ERROR')) return 'error';
  if (u === 'PROCESSED' || u.includes('SUCCESS') || u.includes('COMPLET')) return 'success';
  if (u === 'PENDING' || u.includes('PEND') || u.includes('QUEU')) return 'warning';
  if (u.includes('PROCESS') || u === 'RUNNING') return 'info';
  return 'default';
}

function formatPayloadPreview(raw: string | null | undefined, maxLen: number): string {
  if (raw == null || raw === '') return '–';
  const t = raw.replace(/\s+/g, ' ').trim();
  return t.length <= maxLen ? t : `${t.slice(0, maxLen)}…`;
}

function formatPayloadBlock(raw: string | null | undefined): string {
  if (raw == null || raw === '') return '–';
  try {
    return JSON.stringify(JSON.parse(raw), null, 2);
  } catch {
    return raw;
  }
}

function WebhookJobExpandedBody({
  row,
  t,
  formatDateTime,
  statusLabel,
  surface = 'table',
}: {
  row: WebhookQueueRow;
  t: TFunction;
  formatDateTime: (date?: string | null) => string;
  statusLabel: (s: string) => string;
  surface?: 'table' | 'card';
}) {
  return (
    <Box
      sx={{
        py: 2,
        px: surface === 'table' ? 2 : 0,
        bgcolor: surface === 'table' ? 'action.hover' : undefined,
        borderTop: surface === 'card' ? 1 : 0,
        borderColor: 'divider',
      }}
    >
      <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 600 }}>
        {t('webhooks.detailsTitle')}
      </Typography>
      <Stack spacing={0.75} sx={{ mb: 2 }}>
        <DetailLine label={t('webhooks.detailId')} value={row.id} mono />
        <DetailLine label={t('webhooks.type')} value={row.provider ?? '–'} />
        <DetailLine
          label={t('webhooks.detailStatus')}
          value={statusLabel(String(row.status ?? ''))}
        />
        <DetailLine
          label={t('webhooks.detailAttempts')}
          value={row.attempts != null ? String(row.attempts) : '–'}
        />
        <DetailLine label={t('webhooks.detailReceived')} value={formatDateTime(row.createdAt)} />
        <DetailLine label={t('webhooks.detailProcessed')} value={formatDateTime(row.processedAt)} />
        <DetailLine label={t('webhooks.lastError')} value={row.errorMessage ?? '–'} />
      </Stack>
      <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
        {t('webhooks.payload')}
      </Typography>
      <Box
        component="pre"
        sx={{
          m: 0,
          p: 2,
          borderRadius: 1,
          maxHeight: 420,
          overflow: 'auto',
          bgcolor: (theme) => (theme.palette.mode === 'dark' ? 'grey.950' : 'grey.900'),
          color: 'grey.100',
          fontSize: '0.8rem',
          fontFamily: 'ui-monospace, monospace',
          whiteSpace: 'pre-wrap',
          wordBreak: 'break-word',
        }}
      >
        {formatPayloadBlock(row.payload)}
      </Box>
    </Box>
  );
}

export default function Webhooks() {
  const { t, i18n } = useTranslation();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [timePreset, setTimePreset] = useState<TimePreset>('today');
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo] = useState('');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('');
  const [providerFilter, setProviderFilter] = useState('');
  const [providerOptions, setProviderOptions] = useState<string[]>([]);
  const [searchInput, setSearchInput] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');

  useEffect(() => {
    const id = window.setTimeout(() => setDebouncedSearch(searchInput), 300);
    return () => window.clearTimeout(id);
  }, [searchInput]);

  const locale = i18n.language === 'de' ? 'de-DE' : 'en-US';
  const formatDateTime = (date?: string | null) =>
    date ? new Date(date).toLocaleString(locale, { dateStyle: 'medium', timeStyle: 'short' }) : '–';

  const whereBase = useMemo(
    () =>
      buildEventJobWhere({
        status: statusFilter,
        timePreset,
        customFrom,
        customTo,
        search: debouncedSearch,
      }),
    [statusFilter, timePreset, customFrom, customTo, debouncedSearch],
  );

  const whereFull = useMemo(
    () => mergeProviderIntoWhere(whereBase, providerFilter),
    [whereBase, providerFilter],
  );

  useEffect(() => {
    setProviderFilter('');
    setProviderOptions([]);
  }, [timePreset, customFrom, customTo, statusFilter, debouncedSearch]);

  const { data, loading, error } = useQuery<WebhookQueuesQueryData>(GET_WEBHOOK_QUEUES, {
    variables: { first: 50, where: whereFull },
    errorPolicy: 'all',
  });

  useEffect(() => {
    setPage(0);
  }, [whereFull]);

  useEffect(() => {
    if (loading) return;
    if (providerFilter) return;
    const list = data?.webhookQueues?.nodes ?? [];
    const s = new Set<string>();
    for (const n of list) {
      if (n?.provider) s.add(n.provider);
    }
    setProviderOptions(Array.from(s).sort((a, b) => a.localeCompare(b)));
  }, [data?.webhookQueues?.nodes, loading, providerFilter]);

  const nodes = data?.webhookQueues?.nodes ?? [];
  const rows: WebhookQueueRow[] = (nodes ?? []).filter(Boolean) as WebhookQueueRow[];
  rows.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
  const serverTotal = data?.webhookQueues?.totalCount ?? rows.length;
  const paginatedRows = rows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  const statusLabel = (s: string) => {
    const u = s.toUpperCase();
    if (u === 'FAILED') return t('webhooks.statusFailed');
    if (u === 'PENDING') return t('webhooks.statusPending');
    if (u === 'PROCESSED') return t('webhooks.statusProcessed');
    if (u === 'SKIPPED') return t('webhooks.statusSkipped');
    return s;
  };

  const graphMessage = graphqlErrorMessage(error);

  const toggleRow = (id: string) => {
    setExpandedId((prev) => (prev === id ? null : id));
  };

  return (
    <Box sx={{ width: '100%', minWidth: 0, pb: 2 }}>
      <Typography variant="h4" sx={{ mb: 2 }}>
        {t('webhooks.title')}
      </Typography>

      {graphMessage ? (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {t('webhooks.loadError')}
          <Typography
            component="span"
            variant="body2"
            display="block"
            sx={{ mt: 1, fontFamily: 'monospace' }}
          >
            {graphMessage}
          </Typography>
        </Alert>
      ) : null}

      <Paper
        elevation={0}
        variant="outlined"
        sx={{
          p: 2,
          mb: 2,
          bgcolor: 'background.paper',
        }}
      >
        <Stack spacing={2}>
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            spacing={2}
            useFlexGap
            flexWrap="wrap"
            alignItems={{ xs: 'stretch', md: 'center' }}
          >
            <FormControl size="small" sx={{ minWidth: 160 }}>
              <InputLabel id="webhooks-time-preset">{t('webhooks.timeRange')}</InputLabel>
              <Select<TimePreset>
                labelId="webhooks-time-preset"
                label={t('webhooks.timeRange')}
                value={timePreset}
                onChange={(e) => {
                  setTimePreset(e.target.value as TimePreset);
                  if (e.target.value !== 'custom') {
                    setCustomFrom('');
                    setCustomTo('');
                  }
                }}
              >
                <MenuItem value="all">{t('webhooks.timeRangeAll')}</MenuItem>
                <MenuItem value="today">{t('webhooks.timeRangeToday')}</MenuItem>
                <MenuItem value="last7">{t('webhooks.timeRangeLast7')}</MenuItem>
                <MenuItem value="last30">{t('webhooks.timeRangeLast30')}</MenuItem>
                <MenuItem value="custom">{t('webhooks.timeRangeCustom')}</MenuItem>
              </Select>
            </FormControl>

            <TextField
              size="small"
              type="date"
              label={t('webhooks.receivedFrom')}
              value={customFrom}
              onChange={(e) => setCustomFrom(e.target.value)}
              disabled={timePreset !== 'custom'}
              slotProps={{ inputLabel: { shrink: true } }}
              sx={{ minWidth: 170 }}
            />
            <TextField
              size="small"
              type="date"
              label={t('webhooks.receivedTo')}
              value={customTo}
              onChange={(e) => setCustomTo(e.target.value)}
              disabled={timePreset !== 'custom'}
              slotProps={{ inputLabel: { shrink: true } }}
              sx={{ minWidth: 170 }}
            />

            <FormControl size="small" sx={{ minWidth: 180 }}>
              <InputLabel id="webhooks-status">{t('webhooks.filterStatus')}</InputLabel>
              <Select
                labelId="webhooks-status"
                label={t('webhooks.filterStatus')}
                value={statusFilter || 'all'}
                onChange={(e) => {
                  const v = e.target.value;
                  setStatusFilter(v === 'all' ? '' : (v as WebhookStatus));
                }}
              >
                <MenuItem value="all">{t('webhooks.statusAll')}</MenuItem>
                <MenuItem value="FAILED">{t('webhooks.statusFailed')}</MenuItem>
                <MenuItem value="PENDING">{t('webhooks.statusPending')}</MenuItem>
                <MenuItem value="PROCESSED">{t('webhooks.statusProcessed')}</MenuItem>
                <MenuItem value="SKIPPED">{t('webhooks.statusSkipped')}</MenuItem>
              </Select>
            </FormControl>

            <FormControl size="small" sx={{ minWidth: 200 }}>
              <InputLabel id="webhooks-type-filter">{t('webhooks.filterType')}</InputLabel>
              <Select
                labelId="webhooks-type-filter"
                label={t('webhooks.filterType')}
                value={providerFilter || 'all'}
                onChange={(e) => {
                  const v = e.target.value;
                  setProviderFilter(v === 'all' ? '' : String(v));
                }}
              >
                <MenuItem value="all">{t('webhooks.typeAll')}</MenuItem>
                {providerFilter && !providerOptions.includes(providerFilter) ? (
                  <MenuItem value={providerFilter}>{providerFilter}</MenuItem>
                ) : null}
                {providerOptions.map((p) => (
                  <MenuItem key={p} value={p}>
                    {p}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              size="small"
              placeholder={t('webhooks.searchPlaceholder')}
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              sx={{ flex: 1, minWidth: 220 }}
              slotProps={{
                input: {
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" color="action" />
                    </InputAdornment>
                  ),
                },
              }}
            />
          </Stack>

          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
            <Typography variant="body2" color="text.secondary">
              {t('webhooks.resultsFound', { count: serverTotal })}
            </Typography>
            {statusFilter ? (
              <Chip
                size="small"
                label={`${t('webhooks.filterStatus')}: ${statusLabel(statusFilter)}`}
                onDelete={() => setStatusFilter('')}
              />
            ) : null}
            {providerFilter ? (
              <Chip
                size="small"
                label={`${t('webhooks.filterType')}: ${providerFilter}`}
                onDelete={() => setProviderFilter('')}
              />
            ) : null}
          </Stack>

          {serverTotal > rows.length ? (
            <Typography variant="caption" color="text.secondary">
              {t('webhooks.resultsTruncated', { total: serverTotal, shown: rows.length })}
            </Typography>
          ) : null}
        </Stack>
      </Paper>

      <Paper
        variant="outlined"
        sx={{
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
          maxHeight: { xs: 'calc(100vh - 200px)', md: 'calc(100vh - 320px)' },
          minHeight: 0,
          overflow: 'hidden',
        }}
      >
        <TableContainer
          sx={{ display: { xs: 'none', md: 'block' }, flex: 1, minHeight: 0, overflow: 'auto' }}
        >
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 600 }}>{t('webhooks.received')}</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>{t('webhooks.type')}</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>{t('common.status')}</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>{t('webhooks.processedAt')}</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>{t('webhooks.payload')}</TableCell>
                <TableCell align="right" sx={{ width: 48 }} aria-label="" />
              </TableRow>
            </TableHead>
            <TableBody>
              {loading && rows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6}>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ py: 3, textAlign: 'center' }}
                    >
                      {t('webhooks.loading')}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : null}
              {!loading && paginatedRows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6}>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ py: 3, textAlign: 'center' }}
                    >
                      {t('common.noData')}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : null}
              {paginatedRows.map((row) => {
                const open = expandedId === row.id;
                return (
                  <React.Fragment key={row.id}>
                    <TableRow
                      hover
                      onClick={() => toggleRow(row.id)}
                      sx={{
                        cursor: 'pointer',
                        bgcolor: open ? 'action.selected' : undefined,
                      }}
                    >
                      <TableCell>{formatDateTime(row.createdAt)}</TableCell>
                      <TableCell>{row.provider ?? '–'}</TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          label={statusLabel(String(row.status ?? ''))}
                          color={stateColor(String(row.status ?? ''))}
                        />
                      </TableCell>
                      <TableCell>{formatDateTime(row.processedAt)}</TableCell>
                      <TableCell
                        sx={{
                          maxWidth: 360,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          fontFamily: 'monospace',
                          fontSize: '0.8rem',
                        }}
                      >
                        {formatPayloadPreview(row.payload, 72)}
                      </TableCell>
                      <TableCell align="right" onClick={(e) => e.stopPropagation()}>
                        <IconButton
                          size="small"
                          aria-expanded={open}
                          aria-label={open ? t('webhooks.collapse') : t('webhooks.expand')}
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleRow(row.id);
                          }}
                        >
                          {open ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                        </IconButton>
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell
                        colSpan={6}
                        sx={{ py: 0, borderBottom: open ? 1 : 0, borderColor: 'divider' }}
                      >
                        <Collapse in={open} timeout="auto" unmountOnExit>
                          <WebhookJobExpandedBody
                            row={row}
                            t={t}
                            formatDateTime={formatDateTime}
                            statusLabel={statusLabel}
                            surface="table"
                          />
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  </React.Fragment>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>

        <Box
          sx={{
            display: { xs: 'flex', md: 'none' },
            flexDirection: 'column',
            flex: 1,
            minHeight: 0,
            overflow: 'hidden',
          }}
        >
          <Box
            sx={{
              flex: 1,
              minHeight: 0,
              overflowY: 'auto',
              p: 1.5,
              display: 'flex',
              flexDirection: 'column',
              gap: 1.5,
            }}
          >
            {loading && rows.length === 0 ? (
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ py: 3, textAlign: 'center' }}
              >
                {t('webhooks.loading')}
              </Typography>
            ) : null}
            {!loading && paginatedRows.length === 0 ? (
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ py: 3, textAlign: 'center' }}
              >
                {t('common.noData')}
              </Typography>
            ) : null}
            {paginatedRows.map((row) => {
              const open = expandedId === row.id;
              return (
                <DetailDataCard key={row.id}>
                  <DetailDataCardField label={t('webhooks.received')}>
                    <Typography variant="body2">{formatDateTime(row.createdAt)}</Typography>
                  </DetailDataCardField>
                  <DetailDataCardField label={t('webhooks.type')}>
                    <Typography variant="body2">{row.provider ?? '–'}</Typography>
                  </DetailDataCardField>
                  <DetailDataCardField label={t('common.status')}>
                    <Chip
                      size="small"
                      label={statusLabel(String(row.status ?? ''))}
                      color={stateColor(String(row.status ?? ''))}
                    />
                  </DetailDataCardField>
                  <DetailDataCardField label={t('webhooks.processedAt')}>
                    <Typography variant="body2">{formatDateTime(row.processedAt)}</Typography>
                  </DetailDataCardField>
                  <DetailDataCardField label={t('webhooks.payload')}>
                    <Typography
                      variant="body2"
                      sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}
                    >
                      {formatPayloadPreview(row.payload, 200)}
                    </Typography>
                  </DetailDataCardField>
                  <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                    <IconButton
                      size="small"
                      aria-expanded={open}
                      aria-label={open ? t('webhooks.collapse') : t('webhooks.expand')}
                      onClick={() => toggleRow(row.id)}
                    >
                      {open ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                    </IconButton>
                  </Box>
                  <Collapse in={open} timeout="auto" unmountOnExit>
                    <WebhookJobExpandedBody
                      row={row}
                      t={t}
                      formatDateTime={formatDateTime}
                      statusLabel={statusLabel}
                      surface="card"
                    />
                  </Collapse>
                </DetailDataCard>
              );
            })}
          </Box>
        </Box>

        <TablePagination
          component="div"
          count={rows.length}
          page={page}
          onPageChange={(_, p) => setPage(p)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
          rowsPerPageOptions={[10, 25, 50]}
          labelRowsPerPage={t('common.rowsPerPage')}
          sx={{
            flexShrink: 0,
            borderTop: 1,
            borderColor: 'divider',
            '& .MuiTablePagination-toolbar': { minHeight: 48 },
          }}
        />
      </Paper>
    </Box>
  );
}

function DetailLine({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={{ xs: 0, sm: 2 }} useFlexGap>
      <Typography
        component="span"
        variant="body2"
        color="text.secondary"
        sx={{ minWidth: 140, flexShrink: 0 }}
      >
        {label}
      </Typography>
      <Typography
        component="span"
        variant="body2"
        sx={{ fontFamily: mono ? 'ui-monospace, monospace' : undefined, wordBreak: 'break-word' }}
      >
        {value}
      </Typography>
    </Stack>
  );
}
